import React, { useMemo } from "react";
import { Box, Typography, Stack, Container, Skeleton } from "@mui/material";
import { useGetInvestmentPortfolioQuery } from "../../store/api/finfactor.apislice"; // ← RTK hook
import { useGetPortfolioSummaryQuery } from "../../store/api/portfolio.apislice";
import { parseInvestmentPortfolio } from "../../store/parsers/investmentPortfolio.parser";
import PortfolioPanel from "../../components/FinFactorInvestment/PortfolioPanel";
import CircleIcon from "../../assets/circleicon.svg";
import PortfolioProgressBar from "../../components/FinFactorInvestment/PortfolioProgressBar";
import NodisplayComponent from "../../components/common/NodisplayComponent";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../router/routes.constants";

const InvestmentPortfolioContainer = () => {
  const navigate = useNavigate();

  const {
    data: apiPortfolioData,
    isLoading,
    isFetching,
    isError,
  } = useGetInvestmentPortfolioQuery();

  const { data: portfolioSummaryData } = useGetPortfolioSummaryQuery();

  const portfolioData = useMemo(() => {
    return parseInvestmentPortfolio(apiPortfolioData || {}, portfolioSummaryData?.result);
  }, [apiPortfolioData, portfolioSummaryData]);

  const loading = isLoading || isFetching;

  const handlePanelClick = (title) => {
    const categoryPath = title.toLowerCase().replace(/\s+/g, "-");
    navigate(
      ROUTES.INVESTMENT_PORTFOLIO_DETAILS.replace(":category", categoryPath),
    );
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 2 } }} disableGutters>
        {/* Header Summary Section */}
        <Box
          sx={{
            backgroundColor: "#13172a",
            borderRadius: "24px",
            p: { xs: 2.5, md: 3 },
            border: "1px solid rgba(255, 255, 255, 0.05)",
            mb: 3,
            boxShadow: "0 4px 30px rgba(0, 0, 0, 0.3)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 3
          }}
        >
          {/* LEFT: Icon + Value + Date */}
          <Box sx={{ minWidth: { xs: "100%", md: 220 }, flexShrink: 0 }}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              <Box
                component="img"
                src={CircleIcon}
                sx={{ width: 24, height: 24 }}
              />
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "rgba(255, 255, 255, 0.8)",
                  letterSpacing: "0.5px",
                }}
              >
                {loading ? <Skeleton width={120} /> : portfolioData?.title}
              </Typography>
            </Stack>

            <Typography
              sx={{
                fontSize: { xs: "24px", md: "36px" },
                fontWeight: 700,
                color: "white",
                mb: 1,
                fontFamily: "'Outfit', sans-serif",
                lineHeight: 1.2
              }}
            >
              {loading ? <Skeleton width={180} /> : portfolioData?.totalValue}
            </Typography>

            <Typography
              sx={{
                fontSize: "12px",
                color: "rgba(255, 255, 255, 0.4)",
              }}
            >
              {loading ? <Skeleton width={200} /> : portfolioData?.asOfDate}
            </Typography>
          </Box>

          {/* RIGHT: Progress Bars */}
          <Box
            sx={{
              width: "100%",
              flex: 1,
              minWidth: 0,
              display: "flex",
              justifyContent: { xs: "flex-start", md: "flex-end" },
            }}
          >
            {loading ? (
              <Skeleton
                variant="rectangular"
                height={80}
                sx={{ borderRadius: 2 }}
              />
            ) : (
              <PortfolioProgressBar
                breakdown={portfolioData?.breakdown ?? []}
              />
            )}
          </Box>
        </Box>

        {/* Investment Categories Listing */}
        <Box
          sx={{
            backgroundColor: "#1c2032",
            borderRadius: "24px",
            p: { xs: 2.5, md: 4 },
            border: "1px solid rgba(255, 255, 255, 0.05)",
          }}
        >
          <Box sx={{ "& > :last-child": { mb: 0 } }}>
            {loading ? (
              [...Array(8)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={56}
                  sx={{ borderRadius: 2, mb: 1.5 }}
                />
              ))
            ) : (isError || !portfolioData?.breakdown?.length) ? (
              <NodisplayComponent
                title="No Data Found"
                subtitle={isError ? "We couldn't retrieve your portfolio data at this time. Please try again later." : "You don't have any investments to show."}
                containerSx={{
                  minHeight: "400px",
                  bgcolor: "transparent",
                  px: 0,
                }}
              />
            ) : (
              portfolioData?.breakdown?.map((item) => (
                <PortfolioPanel
                  key={item.id}
                  title={item.label}
                  count={item.count}
                  value={item.value}
                  change={item.growth}
                  isPositive={item.isPositive}
                  percentage={item.percentage}
                  color={item.color}
                  onClick={() => handlePanelClick(item.label)}
                />
              ))
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default InvestmentPortfolioContainer;
